import { initReactI18next } from 'react-i18next';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

import { DEFAULT_LANGUAGE_CODE } from '~common/constants';
import { TenantInfo } from '~types/tenant.type';

const RESOURCE_LANGUAGES = [
  'en-US',
  'es-MX',
  'es-DO',
  'es-PA',
  'es-CO',
  'de',
  'fr-FR',
  'fr-CA',
  'sv-SE',
];

export const toResourceLanguage = (code: string): string | undefined => {
  if (RESOURCE_LANGUAGES.includes(code)) {
    return code;
  }
  const [language] = code.split('-');
  return RESOURCE_LANGUAGES.find(
    (resource) => resource.split('-')[0] === language,
  );
};

export const resolveTenantLanguages = (tenant: TenantInfo | null) => {
  if (!tenant?.isWhiteLabel) {
    return null;
  }

  const supported = tenant.locale.supported
    .map(toResourceLanguage)
    .filter((language): language is string => !!language);
  const fallback = toResourceLanguage(tenant.locale.default) ?? supported[0];
  if (!fallback) {
    return null;
  }

  return {
    fallback,
    supported: Array.from(new Set([fallback, ...supported])),
  };
};

let initialisedWith: string | undefined;

/**
 * Safe to call on every render: it initialises again only when its input
 * changes, i.e. a failed tenant-info retried into a white-label tenant. The
 * unavailable pages pass `storeLanguage: false`: they offer no language
 * choice, so the detected browser language must not be stored as one.
 */
export const initI18n = (
  tenant: TenantInfo | null = null,
  { storeLanguage = true } = {},
) => {
  const languages = resolveTenantLanguages(tenant);
  const key = JSON.stringify([languages, storeLanguage]);
  if (key === initialisedWith) {
    return;
  }
  initialisedWith = key;

  i18n
    .use(Backend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      debug: process.env.NODE_ENV === 'development',
      fallbackLng: languages?.fallback ?? DEFAULT_LANGUAGE_CODE,
      ...(languages && { supportedLngs: languages.supported }),
      detection: {
        ...(languages && { order: ['querystring', 'localStorage'] }),
        ...(!storeLanguage && { caches: [] }),
      },
    });
};

export const filterSupportedLanguages = <T extends { value: string }>(
  options: T[],
): T[] => {
  const { supportedLngs } = i18n.options;
  return Array.isArray(supportedLngs)
    ? options.filter((option) => supportedLngs.includes(option.value))
    : options;
};

export default i18n;
